/**
 * Tiles Workshop — product page scripts
 * Loaded only on the product template (layout/theme.liquid). No dependencies: the lightbox,
 * drawers and popovers are Impact's own elements; this file only wires the TW blocks to them.
 *
 * Contents (in file order)
 *  - twScrollToAnchor: header-aware scrolling shared by the nav and page anchors
 *  - <tw-anchor-nav>: sticky tab bar built from the blocks on the page
 *  - <tw-disclosure>: read-more for long copy
 *  - Gallery: click-to-open lightbox, photo grid lightbox, lightbox padding
 *  - Variant preview zoom, header tracker (--tw-header-bottom), dismissible notes
 *  - Drag-to-scroll rows, Save & Share, checklist fold, related-events tabs, meta-row anchors
 *  - Sticky bar CTA, booking sheet, collapsed timeline, see-more fold, gallery counter
 */
/**
 * Scrolls a block into place under the sticky nav.
 *
 * scrollIntoView cannot be trusted here: the offset it reads (scroll-margin) is measured before
 * the scroll, while Impact's header hides as soon as the page moves down, so the block lands a
 * header's height too low. On top of that the theme sets scroll-padding on <html>, which adds
 * the static header height again. We compute the destination ourselves, scroll, and once the
 * header has settled we correct any leftover difference.
 */
function twScrollToAnchor(target, align = 'start') {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const behavior = reduced ? 'auto' : 'smooth';

  // Impact's header or the TW header, whichever this page shows.
  const header = Array.from(document.querySelectorAll('store-header.header, store-header.tw-hdr')).find((el) => el.offsetParent) || null;
  const nav = document.querySelector('tw-anchor-nav');
  const navHeight = nav ? nav.offsetHeight : 0;

  // Where the header will be once we arrive: Impact hides it while scrolling down and shows it
  // while scrolling up. Predicting that up front gives one clean motion with no correction jump.
  const currentTop = target.getBoundingClientRect().top;
  const headerHeight = header ? header.offsetHeight : 0;
  const headerNow = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
  const goingDown = currentTop > headerNow + navHeight + 16;
  const headerAfter = goingDown ? 0 : headerHeight;

  let desired = Math.round(headerAfter + navHeight + 16);
  if (align === 'center') {
    const height = target.getBoundingClientRect().height;
    if (height < window.innerHeight * 0.7) desired = Math.round((window.innerHeight - height) / 2);
  }

  window.scrollBy({ top: Math.round(currentTop - desired), behavior });

  // Only if the prediction was wrong (e.g. a header set to stay visible) nudge smoothly afterwards.
  setTimeout(() => {
    const headerBottom = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
    const want = align === 'center' ? desired : Math.round(headerBottom + navHeight + 16);
    const delta = Math.round(target.getBoundingClientRect().top - want);
    if (Math.abs(delta) > 8) window.scrollBy({ top: delta, behavior });
  }, reduced ? 50 : 700);
}

/**
 * TW anchor nav
 * -----------------------------------------------------------------------------------------------
 * Builds the tab list from the [data-tw-anchor] blocks on the page. When blocks are reordered,
 * added or removed the nav follows automatically — there is no list to maintain by hand.
 */
class TwAnchorNav extends HTMLElement {
  connectedCallback() {
    this.build();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.resizeObserver?.disconnect();
  }

  build() {
    this.list = this.querySelector('.tw-nav__list');
    if (!this.list) return;

    const scope = this.closest('.product') || this.closest('.shopify-section') || document;
    this.targets = Array.from(scope.querySelectorAll('[data-tw-anchor]')).filter((el) => !this.contains(el));

    if (this.targets.length === 0) {
      this.hidden = true;
      return;
    }
    this.hidden = false;

    this.list.replaceChildren();
    this.links = this.targets.map((target) => {
      const link = document.createElement('a');
      link.href = `#${target.id}`;
      link.className = 'tw-nav__link';
      link.textContent = target.dataset.twNavLabel || target.id;
      link.addEventListener('click', (event) => {
        event.preventDefault();
        this.scrollTo(target);
      });
      this.list.appendChild(link);
      return link;
    });

    this.publishHeight();
    this.observe();

    this.resizeObserver?.disconnect();
    this.resizeObserver = new ResizeObserver(() => this.publishHeight());
    this.resizeObserver.observe(this);
  }

  /**
   * We do not compute the scroll offset ourselves — the targets carry scroll-margin-top and the
   * browser handles it. That stays correct whether or not the nav is stuck, and arriving from
   * the address bar with a #hash behaves the same way.
   */
  publishHeight() {
    document.documentElement.style.setProperty('--tw-nav-height', `${this.offsetHeight}px`);
  }

  get offset() {
    const header = parseFloat(getComputedStyle(this).getPropertyValue('--sticky-area-height'));
    return (Number.isNaN(header) ? 0 : header) + this.offsetHeight + 16;
  }

  scrollTo(target) {
    twScrollToAnchor(target, 'start');
  }

  observe() {
    this.observer?.disconnect();
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) this.setActive(entry.target);
        });
      },
      { rootMargin: `-${Math.round(this.offset)}px 0px -60% 0px`, threshold: 0 }
    );
    this.targets.forEach((target) => this.observer.observe(target));
  }

  setActive(target) {
    const index = this.targets.indexOf(target);
    this.links.forEach((link, i) => link.classList.toggle('is-active', i === index));

    // On phones the tab row scrolls sideways; keep the active tab where the thumb can see it.
    const active = this.links[index];
    if (active && this.list.scrollWidth > this.list.clientWidth) {
      const left = active.offsetLeft - (this.list.clientWidth - active.offsetWidth) / 2;
      this.list.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
    }
  }
}

if (!customElements.get('tw-anchor-nav')) {
  customElements.define('tw-anchor-nav', TwAnchorNav);
}

/* Rebuild the nav when the theme editor re-renders a section. */
document.addEventListener('shopify:section:load', () => {
  document.querySelectorAll('tw-anchor-nav').forEach((nav) => nav.build());
});

/**
 * TW disclosure — a box that truncates long copy and expands it on demand.
 * When the copy is already short no button is rendered, so no pointless control appears.
 */
class TwDisclosure extends HTMLElement {
  connectedCallback() {
    this.body = this.querySelector('[data-tw-disclosure-body]');
    this.toggle = this.querySelector('[data-tw-disclosure-toggle]');
    if (!this.body || !this.toggle) return;

    this.toggle.addEventListener('click', () => {
      this.setOpen(this.toggle.getAttribute('aria-expanded') !== 'true');
    });

    this.sync();
    this.resizeObserver = new ResizeObserver(() => this.sync());
    this.resizeObserver.observe(this.body);
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
  }

  sync() {
    const collapsed = parseFloat(getComputedStyle(this).getPropertyValue('--tw-collapsed')) || 120;
    const overflows = this.body.scrollHeight > collapsed + 8;
    this.toggle.hidden = !overflows;

    // With nothing to collapse the body is forced open. That has to be released when overflow
    // returns after a resize, otherwise the box stays expanded while the toggle reads "Read more".
    if (!overflows) {
      this.setOpen(true);
    } else if (this.forcedOpen) {
      this.setOpen(false);
    }

    this.forcedOpen = !overflows;
  }

  setOpen(open) {
    this.classList.toggle('is-open', open);
    this.toggle.setAttribute('aria-expanded', String(open));
    this.toggle.textContent = open ? this.toggle.dataset.labelLess : this.toggle.dataset.labelMore;
  }
}

if (!customElements.get('tw-disclosure')) {
  customElements.define('tw-disclosure', TwDisclosure);
}

/**
 * Gallery: clicking any image opens the lightbox, so no separate zoom icon is needed.
 * Impact's <product-gallery> element listens for 'lightbox:open' and calls openZoom(index);
 * we dispatch that same event with the index of the tile that was clicked.
 *
 * We also give PhotoSwipe horizontal padding — Impact sets none, so the image covered the whole
 * screen and the previous/next images were never visible.
 */
/**
 * Resolves a gallery index in the same space openZoom() uses.
 *
 * openZoom() builds its slide list from
 *   .product-gallery__media[data-media-type="image"]:not([hidden]) > img
 * so anything we hand it must count visible IMAGE tiles only. Counting raw tiles skews as soon
 * as the product carries a video or a variant filter hides media, and the wrong slide opens.
 */
function twGalleryImages(gallery) {
  return Array.from(gallery.querySelectorAll('.product-gallery__media[data-media-type="image"]:not([hidden])'));
}

function twIndexOfMediaId(gallery, mediaId) {
  return twGalleryImages(gallery).findIndex((tile) => tile.dataset.mediaId === String(mediaId));
}

document.addEventListener('click', (event) => {
  const tile = event.target.closest('.product-gallery__media');
  if (!tile) return;

  // Leave the existing controls alone (zoom button, play video, AR).
  if (event.target.closest('button, a, [role="button"]')) return;
  if (tile.dataset.mediaType && tile.dataset.mediaType !== 'image') return;

  const gallery = tile.closest('product-gallery');
  if (!gallery || !gallery.hasAttribute('allow-zoom')) return;

  const index = twGalleryImages(gallery).indexOf(tile);
  if (index < 0) return;

  twLastZoomOrigin = tile;
  applyLightboxPadding(gallery);
  tile.dispatchEvent(new CustomEvent('lightbox:open', { bubbles: true, detail: { index } }));
});

let twLastZoomOrigin = null;

/**
 * TW photo grid (tw_photo_grid): every tile opens the theme's own PhotoSwipe, fed with all the
 * grid's photos — the hidden ones included — so a 5-tile grid can page through 30 photos.
 * The lightbox instance lives on <product-gallery>; we borrow it rather than ship a second one.
 */
document.addEventListener('click', (event) => {
  const cell = event.target.closest('[data-tw-photo]');
  if (!cell) return;
  const grid = cell.closest('[data-tw-photo-grid]');
  const gallery = document.querySelector('product-gallery');
  if (!grid || !gallery || !gallery.photoswipe) return;

  const images = Array.from(grid.querySelectorAll('[data-tw-photo] img'));
  const dataSource = images.map((image) => ({
    thumbnailElement: image,
    src: image.src,
    srcset: image.srcset,
    msrc: image.currentSrc || image.src,
    width: parseInt(image.getAttribute('width'), 10),
    height: parseInt(image.getAttribute('height'), 10),
    alt: image.alt,
    thumbCropped: true
  }));

  twLastZoomOrigin = cell;
  applyLightboxPadding(gallery);
  gallery.photoswipe.loadAndOpen(parseInt(cell.dataset.twPhoto, 10) || 0, dataSource);
});

/**
 * Lamp pictures (TW variant picker -> pictures from a metaobject).
 *
 * Those pictures are deliberately kept out of the product's media, so the gallery's lightbox knows
 * nothing about them. The zoom button on a picture button opens the same PhotoSwipe with a set
 * built from that option's own pictures: a reader can page through the lamps and nothing else.
 */
document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-tw-lamp-zoom]');
  if (!button) return;
  // The same lightbox serves the picture buttons and the lamp price block, so the set is whichever
  // container the button sits in rather than one hard-coded class.
  const list = button.closest('[data-tw-zoom-set]') || button.closest('.variant-picker__option-values');
  const gallery = document.querySelector('product-gallery');
  if (!list || !gallery || !gallery.photoswipe) return;

  event.preventDefault();
  event.stopPropagation();

  const ITEM = '[data-tw-zoom-item], .thumbnail-swatch';
  const images = Array.from(list.querySelectorAll(ITEM)).map((item) => item.querySelector('img')).filter(Boolean);
  const clicked = button.closest(ITEM)?.querySelector('img');
  const dataSource = images.map((image) => ({
    thumbnailElement: image,
    src: image.src,
    srcset: image.srcset,
    msrc: image.currentSrc || image.src,
    width: parseInt(image.getAttribute('width'), 10),
    height: parseInt(image.getAttribute('height'), 10),
    alt: image.alt,
    thumbCropped: true
  }));
  if (!dataSource.length) return;

  twLastZoomOrigin = button.closest(ITEM);
  applyLightboxPadding(gallery);
  gallery.photoswipe.loadAndOpen(Math.max(0, images.indexOf(clicked)), dataSource);
});

function applyLightboxPadding(gallery) {
  const percent = parseFloat(gallery.dataset.twLightboxPadding);

  try {
    const lightbox = gallery.photoswipe;
    if (!lightbox || !lightbox.options) return;
    const side = Number.isNaN(percent) ? 0 : Math.round((window.innerWidth * percent) / 100);
    lightbox.options.padding = { top: 40, bottom: 40, left: side, right: side };

    const opacity = parseFloat(gallery.dataset.twLightboxOpacity);
    if (!Number.isNaN(opacity)) lightbox.options.bgOpacity = opacity / 100;

    const anim = gallery.dataset.twLightboxAnimation || 'fade';
    lightbox.options.showHideAnimationType = anim === 'origin' ? 'zoom' : anim;

    // In 'origin' mode grow from the CLICKED element rather than its counterpart in the gallery.
    if (anim === 'origin' && !lightbox._twBoundsFilter) {
      lightbox._twBoundsFilter = true;
      lightbox.addFilter('thumbBounds', (bounds) => {
        if (!twLastZoomOrigin || !twLastZoomOrigin.isConnected) return bounds;
        const rect = twLastZoomOrigin.getBoundingClientRect();
        return { x: rect.left, y: rect.top, w: rect.width };
      });
    }
  } catch (error) {
    /* Fall back to the default silently if Impact's internals change. */
  }
}

/* Apply the same padding when the lightbox is opened from the zoom icon. */
document.addEventListener('lightbox:open', (event) => {
  const gallery = event.target.closest?.('product-gallery');
  if (gallery) applyLightboxPadding(gallery);
}, true);

/**
 * TW variant preview — shows the image of the selected variant.
 * Every option image is already printed on the page and only the selected one is visible, so
 * switching variants needs no extra request. Listens to Impact's 'variant:change' event.
 */
class TwVariantPreview extends HTMLElement {
  connectedCallback() {
    this.optionIndex = parseInt(this.dataset.optionIndex, 10);
    if (Number.isNaN(this.optionIndex)) return;
    this.onVariantChange = (event) => this.show(event.detail?.variant);
    document.addEventListener('variant:change', this.onVariantChange);
  }

  disconnectedCallback() {
    document.removeEventListener('variant:change', this.onVariantChange);
  }

  show(variant) {
    if (!variant || !Array.isArray(variant.options)) return;
    const value = variant.options[this.optionIndex];
    if (value == null) return;

    this.querySelectorAll('[data-tw-vp-for]').forEach((item) => {
      item.hidden = item.dataset.twVpFor !== value;
    });

    const label = this.querySelector('[data-tw-vp-value]');
    if (label) label.textContent = value;
  }
}

if (!customElements.get('tw-variant-preview')) {
  customElements.define('tw-variant-preview', TwVariantPreview);
}


/**
 * Zoom trigger — used both in the variant preview and on the thumbnail buttons.
 * Thumbnails live inside a <label>, so we stop the event; otherwise zooming would also
 * select that variant.
 */
document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-tw-vp-zoom]');
  if (!trigger) return;

  event.preventDefault();
  event.stopPropagation();

  const source = trigger.dataset.twVpMediaId ? trigger : trigger.closest('[data-tw-vp-media-id]');
  if (!source) return;

  // Stay inside this product's own section; a page can render more than one gallery.
  const scope = trigger.closest('.shopify-section') || document;
  const gallery = scope.querySelector('product-gallery[allow-zoom]') || document.querySelector('product-gallery[allow-zoom]');
  if (!gallery) return;

  const index = twIndexOfMediaId(gallery, source.dataset.twVpMediaId);
  if (index < 0) return;

  twLastZoomOrigin = trigger.closest('.thumbnail-swatch, .tw-vp__item, .product-gallery__media') || trigger;
  applyLightboxPadding(gallery);
  gallery.dispatchEvent(new CustomEvent('lightbox:open', { bubbles: true, detail: { index } }));
}, true);

/**
 * Publishes the header's real bottom edge as --tw-header-bottom.
 *
 * Impact's header hides on scroll down and returns on scroll up. The static
 * --sticky-area-height does not know that, so it reserved space even while the header was gone
 * and left a gap above the tabs. Measuring the real position stays correct despite the animation.
 */
(() => {
  // The element that actually moves is <store-header class="header">: it sits inside the sticky
  // section and takes the hide-on-scroll transform, so its rect mirrors the visual position.
  // querySelector resolves by document order, so the candidates are tried one at a time.
  const header =
    document.querySelector('store-header.header') ||
    document.querySelector('store-header.tw-hdr') ||
    document.querySelector('.shopify-section--header') ||
    document.querySelector('.shopify-section-group-header-group');
  if (!header) return;

  let frame = null;
  let settleUntil = 0;
  let last = -1;

  const publish = () => {
    frame = null;
    const bottom = Math.max(0, Math.round(header.getBoundingClientRect().bottom));
    if (bottom !== last) {
      last = bottom;
      document.documentElement.style.setProperty('--tw-header-bottom', `${bottom}px`);
    }
    // The header slides in and out and keeps moving after scrolling stops, so we keep measuring
    // for a short while after the last scroll event.
    if (performance.now() < settleUntil) frame = requestAnimationFrame(publish);
  };

  const schedule = (extend = true) => {
    if (extend) settleUntil = performance.now() + 600;
    if (frame === null) frame = requestAnimationFrame(publish);
  };

  publish();
  window.addEventListener('scroll', () => schedule(), { passive: true });
  window.addEventListener('resize', () => schedule(), { passive: true });
  header.addEventListener('transitionend', () => schedule(false));
  new ResizeObserver(() => schedule(false)).observe(header);
})();

/**
 * Dismissible note boxes (tw_note -> "Allow dismissing").
 *
 * A dismissal is remembered for the session: it survives a reload but resets when the browser
 * closes. We deliberately do not remember it in the theme editor, otherwise a merchant who
 * closed the box once could never edit it again.
 */
(() => {
  const storageKey = (id) => `tw-note:${id}`;

  const remember = (note) => {
    if (window.Shopify && window.Shopify.designMode) return;
    try {
      sessionStorage.setItem(storageKey(note.dataset.twNote), '1');
    } catch (error) {
      // Private windows or blocked site data can throw; give up quietly.
    }
  };

  const restore = () => {
    if (window.Shopify && window.Shopify.designMode) return;
    document.querySelectorAll('[data-tw-note]').forEach((note) => {
      try {
        if (sessionStorage.getItem(storageKey(note.dataset.twNote)) === '1') note.hidden = true;
      } catch (error) {
        // Reading can throw too; leaving the box visible is the safe default.
      }
    });
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-tw-note-close]');
    if (!trigger) return;

    const note = trigger.closest('[data-tw-note]');
    if (!note) return;

    note.hidden = true;
    remember(note);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', restore, { once: true });
  } else {
    restore();
  }

  // Re-apply the state when the theme editor reloads a block.
  document.addEventListener('shopify:section:load', restore);
})();

/**
 * Save & Share (tw_event_title -> "Show Save and Share").
 *
 * Save has no account behind it: the handle goes into localStorage so the heart stays filled
 * on this browser. Share prefers the device share sheet (navigator.share); the popover menu is
 * the fallback for desktop browsers without one. The popover's own toggle is bound to the
 * button's aria-controls, so when the share sheet is available we stop the click before it
 * reaches that handler.
 */
(() => {
  const KEY = 'tw:saved';
  const read = () => { try { return JSON.parse(localStorage.getItem(KEY)) || []; } catch { return []; } };
  const write = (list) => { try { localStorage.setItem(KEY, JSON.stringify(list)); } catch {} };

  const paint = (button, saved) => {
    button.classList.toggle('is-saved', saved);
    button.setAttribute('aria-pressed', String(saved));
    const label = button.querySelector('span');
    if (label) label.textContent = saved ? button.dataset.labelSaved : button.dataset.labelSave;
  };

  const init = () => {
    const saved = read();
    document.querySelectorAll('[data-tw-save]').forEach((button) => paint(button, saved.includes(button.dataset.twSave)));
  };
  init();
  document.addEventListener('shopify:section:load', init);

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-tw-save]');
    if (!button) return;
    const handle = button.dataset.twSave;
    let saved = read();
    const on = !saved.includes(handle);
    saved = on ? [...saved, handle] : saved.filter((h) => h !== handle);
    write(saved);
    paint(button, on);
  });

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-tw-share]');
    if (!button || typeof navigator.share !== 'function') return;
    event.stopImmediatePropagation();
    event.preventDefault();
    navigator.share({ title: button.dataset.shareTitle, url: button.dataset.shareUrl }).catch(() => {});
  }, true);

  document.addEventListener('click', async (event) => {
    const item = event.target.closest('[data-tw-copy]');
    if (!item) return;
    try {
      await navigator.clipboard.writeText(item.dataset.twCopy);
      const label = item.querySelector('span');
      const original = label.textContent;
      label.textContent = item.dataset.labelCopied;
      item.classList.add('is-copied');
      setTimeout(() => { label.textContent = original; item.classList.remove('is-copied'); }, 1800);
    } catch {
      window.prompt('Copy this link', item.dataset.twCopy);
    }
  });
})();

/**
 * Checklist fold (tw_checklist -> "Items shown before Show all"): items past the limit are
 * hidden until the button is pressed, then the button turns into "Show less".
 */
(() => {
  // On a phone a block that folds under its heading shows its whole list once opened (see tw-product.css).
  const phone = window.matchMedia('(max-width: 749px)');
  const setup = () => {
    document.querySelectorAll('[data-tw-checklist]').forEach((box) => {
      const visible = parseInt(box.dataset.twVisible, 10) || 0;
      const items = Array.from(box.querySelectorAll('.tw-checklist__body li'));
      const open = box.classList.contains('is-open') || (phone.matches && Boolean(box.closest('[data-tw-acc-full]')));
      // One line past the limit is left in place to fade out; the rest go.
      const peek = box.dataset.twPeek !== 'false';
      items.forEach((li, i) => {
        const cut = !open && i >= visible;
        li.classList.toggle('is-peek', peek && cut && i === visible);
        li.classList.toggle('is-folded', cut && !(peek && i === visible));
      });
    });
  };
  setup();
  phone.addEventListener('change', setup);
  document.addEventListener('shopify:section:load', setup);

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-tw-checklist-toggle]');
    if (!toggle) return;
    const box = toggle.closest('[data-tw-checklist]');
    const open = !box.classList.contains('is-open');
    box.classList.toggle('is-open', open);
    toggle.setAttribute('aria-expanded', String(open));
    toggle.textContent = open ? toggle.dataset.labelLess : toggle.dataset.labelMore;
    setup();
  });
})();

/**
 * Rich text fold (event_content with "Lines shown"): the button only appears when the text is
 * really longer than the limit. Measured again on resize, since lines rewrap.
 */
(() => {
  const measure = () => {
    document.querySelectorAll('[data-tw-fold]').forEach((box) => {
      if (box.classList.contains('is-open')) return;
      const body = box.querySelector('.tw-fold__body');
      const button = box.querySelector('[data-tw-fold-toggle]');
      box.classList.remove('is-fits');
      const fits = body.scrollHeight <= body.clientHeight + 4;
      box.classList.toggle('is-fits', fits);
      button.hidden = fits;
    });
  };
  measure();
  window.addEventListener('load', measure);
  window.addEventListener('resize', measure, { passive: true });
  document.addEventListener('shopify:section:load', measure);
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-tw-fold-toggle]');
    if (!button) return;
    const box = button.closest('[data-tw-fold]');
    const open = !box.classList.contains('is-open');
    box.classList.toggle('is-open', open);
    button.setAttribute('aria-expanded', String(open));
    button.textContent = open ? button.dataset.labelLess : button.dataset.labelMore;
  });
})();

/**
 * Related events tabs (sections/tw-related-events.liquid).
 */
document.addEventListener('click', (event) => {
  const tab = event.target.closest('[data-tw-tab]');
  if (!tab) return;
  const root = tab.closest('[data-tw-tabs]');
  root.querySelectorAll('[data-tw-tab]').forEach((t) => {
    const on = t === tab;
    t.classList.toggle('is-active', on);
    t.setAttribute('aria-selected', String(on));
  });
  root.querySelectorAll('[data-tw-panel]').forEach((p) => { p.hidden = p.dataset.twPanel !== tab.dataset.twTab; });
});

/**
 * Meta row links, and the venue's map link when set to scroll, that point at a block on this page
 * (#tw-location): open the fold if the target
 * is behind "See more", then let the browser scroll — the targets carry scroll-margin-top.
 */
document.addEventListener('click', (event) => {
  const link = event.target.closest('.tw-meta__link[href*="#tw-"], .tw-venue__link[href^="#tw-"]');
  if (!link) return;
  const hash = link.getAttribute('href').split('#')[1];
  const target = hash && document.getElementById(hash);
  if (!target) return;
  event.preventDefault();
  const more = target.closest('[data-tw-more]');
  if (more) twRevealMore(more);
  // The venue block lands near the middle of the screen, not tucked under the header.
  twScrollToAnchor(target, hash === 'tw-location' ? 'center' : 'start');
  history.replaceState(null, '', '#' + hash);
});

/**
 * The rating beside the price scrolls to the Judge.me reviews. The product templates carry
 * Judge.me as app blocks, so there is no fixed id to point at: the first Judge.me widget on the
 * page is the target, taken as its whole section. If there is none, or it has no height yet, the
 * click does nothing.
 */
document.addEventListener('click', (event) => {
  const link = event.target.closest('[data-tw-reviews-link]');
  if (!link) return;
  const widget = document.querySelector('#judgeme_product_reviews, .jdgm-review-widget, .jdgm-cards-carousel, [id*="judge_me_reviews"]');
  const target = widget?.closest('.shopify-section') || widget;
  if (!target || !target.offsetHeight) return;
  event.preventDefault();
  twScrollToAnchor(target, 'start');
});

/**
 * Event sticky bar: the button takes the reader back to the pickers (variant picker, else the form).
 * When the booking sheet is active on this screen size it opens the sheet instead.
 */
document.addEventListener('click', (event) => {
  const cta = event.target.closest('[data-tw-sticky-cta]');
  if (!cta) return;
  const sheet = document.querySelector('[data-tw-sheet]');
  if (sheet && sheet.dataset.twSheetActive === '1') { sheet.show(); return; }
  // Kits: the bar's button adds to cart by pressing the page's own Add to cart button.
  if (cta.hasAttribute('data-tw-sticky-add')) {
    const add = Array.from(document.querySelectorAll('buy-buttons button[type="submit"], buy-buttons .button'))
      .find((b) => !b.closest('product-quick-add'));
    if (add) { add.click(); return; }
  }
  const target = document.querySelector('.product-info .variant-picker') || document.querySelector('.product-info .product-info__variant-picker') || document.querySelector('.product-info');
  if (target) twScrollToAnchor(target, 'start');
});

/**
 * Ghost drawers.
 *
 * Every variant change makes the theme re-render this section and parse the whole thing with
 * createContextualFragment. Custom elements in that fragment are upgraded even though the fragment
 * never enters the page, so each copy of a drawer runs its own connectedCallback and binds a click
 * listener for its id on document.body — one more listener per change. The next tap on a trigger
 * therefore opens the real drawer AND every copy made since, and a copy appends itself to the body
 * empty: the reader meets a blank panel, closes it, meets the next one, and so on.
 *
 * Nothing in the page can stop the fragment being parsed, so the copies are caught where they
 * become visible: the moment one appends itself to the body, a drawer that shares its id with the
 * real one is closed and removed. The real ones are marked when the page loads.
 */
(() => {
  const SELECTOR = 'x-drawer[id], x-popover[id]';

  const markReal = () => {
    document.querySelectorAll(SELECTOR).forEach((element) => {
      if (!element.dataset.twReal) element.dataset.twReal = '1';
    });
  };

  markReal();
  document.addEventListener('shopify:section:load', markReal);

  new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (node.nodeType !== 1) continue;
        const drawers = node.matches?.(SELECTOR) ? [node] : Array.from(node.querySelectorAll?.(SELECTOR) || []);
        for (const drawer of drawers) {
          if (drawer.dataset.twReal) continue;
          const real = document.querySelector(`[data-tw-real][id="${CSS.escape(drawer.id)}"]`);
          // A drawer with an id of its own is somebody else's business; only a twin is a ghost.
          if (!real || real === drawer) { drawer.dataset.twReal = '1'; continue; }
          drawer.hide?.();
          drawer.remove();
        }
      }
    }
  }).observe(document.body, { childList: true });
})();

/**
 * Booking sheet (section -> "Booking sheet on mobile").
 *
 * Below 1000px the variant picker, quantity and Add to cart blocks are moved into the bottom
 * sheet and a trigger card takes their place. The blocks keep their form= attributes, so the
 * product form is untouched. Impact re-renders those blocks on variant change by looking inside
 * <product-rerender>; the moved copies live outside it, so we repeat that swap ourselves from
 * the same HTML fragment. Everything is put back when the viewport grows past the breakpoint.
 */
(() => {
  let sheet = document.querySelector('[data-tw-sheet]');
  if (!sheet) return;
  const template = document.querySelector('[data-tw-sheet-trigger]');
  const formId = sheet.dataset.form;
  const form = document.getElementById(formId);
  if (!form || !template) return;

  const mq = window.matchMedia('(max-width: 999px)');
  const homes = new Map(); // moved node -> placeholder left at its original position
  let card = null;
  let price = parseInt(sheet.dataset.price, 10) || 0;

  const money = (cents) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: sheet.dataset.currency || 'USD', minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(cents / 100);
    } catch { return (cents / 100).toFixed(2); }
  };
  const blocks = () => {
    const info = document.querySelector('.product-info');
    const find = (type) => (info && info.querySelector(`[data-block-type="${type}"]`))
      || sheet.querySelector(`[data-block-type="${type}"]`)
      || null;
    return {
      picker: find('variant-picker'),
      qty: find('quantity-selector'),
      buy: find('buy-buttons'),
      notes: Array.from(document.querySelectorAll('[data-tw-in-sheet]'))
    };
  };

  const move = (node, into) => {
    if (!node || homes.has(node)) return;
    const home = document.createElement('div');
    home.hidden = true;
    home.dataset.twHome = '1';
    node.before(home);
    homes.set(node, home);
    into.appendChild(node);
  };

  const pickText = () => {
    // The month row only narrows the dates below it, so it is left out of the summary line.
    const spans = sheet.querySelectorAll('.variant-picker__option:not(.tw-vp-months) .variant-picker__option-info .h-stack > span');
    const parts = Array.from(spans).map((s) => s.textContent.trim()).filter(Boolean);
    return parts.join(' · ');
  };

  const qtyValue = () => parseInt(form.querySelector('input[name="quantity"]')?.value || document.querySelector(`input[name="quantity"][form="${formId}"]`)?.value || '1', 10) || 1;

  const refresh = () => {
    const text = pickText();
    document.querySelectorAll('[data-tw-sheet-pick]').forEach((el) => { el.textContent = text || '\u00a0'; });
    const total = sheet.querySelector('[data-tw-sheet-total]');
    if (total) total.textContent = money(price * qtyValue());
  };

  const activate = () => {
    if (sheet.dataset.twSheetActive === '1') return;
    const b = blocks();
    if (!b.picker && !b.buy) return;
    sheet.dataset.twSheetActive = '1';

    // The trigger card takes the picker's place (or the buy button's, if there is no picker).
    // A card left over from an earlier pass would read as a second booking row.
    document.querySelectorAll('.tw-sheet-card').forEach((stray) => stray.remove());
    card = template.content.firstElementChild.cloneNode(true);
    (b.picker || b.buy).before(card);

    const group = b.qty?.closest('.product-info__block-group');
    move(b.picker, sheet.querySelector('[data-tw-sheet-body]'));
    b.notes.forEach((n) => move(n, sheet.querySelector('[data-tw-sheet-notes]')));
    if (b.qty) {
      move(b.qty, sheet.querySelector('[data-tw-sheet-qty]'));
      sheet.querySelector('[data-tw-sheet-guests-row]').hidden = false;
    }
    move(b.buy, sheet.querySelector('[data-tw-sheet-buy]'));
    if (group && !group.querySelector('.product-info__block-item')) group.classList.add('tw-sheet-emptied');
    frame();
    refresh();
  };

  const deactivate = () => {
    if (sheet.dataset.twSheetActive !== '1') return;
    sheet.dataset.twSheetActive = '0';
    unframe();
    // A placeholder dies whenever the theme re-renders the whole buying column (it does that for a
    // product change). Replacing a detached placeholder would take the block out of the page
    // altogether — which is how the sheet ended up empty with no way back.
    const info = document.querySelector('.product-info');
    homes.forEach((home, node) => {
      if (home.isConnected) { home.replaceWith(node); return; }
      // The column was rebuilt: it already holds a fresh block of this type, so the one we carried
      // is a stale copy and goes; if it holds none, ours is the only one left and goes back in.
      const type = node.getAttribute('data-block-type');
      const rebuilt = type && info && info.querySelector(`[data-block-type="${type}"]`);
      if (rebuilt) { node.remove(); return; }
      if (info && !info.contains(node)) info.appendChild(node);
    });
    homes.clear();
    document.querySelectorAll('.tw-sheet-emptied').forEach((g) => g.classList.remove('tw-sheet-emptied'));
    sheet.querySelector('[data-tw-sheet-guests-row]').hidden = true;
    card?.remove();
    card = null;
  };

  // The booking area reads as one panel: everything from the chosen block down to the trigger
  // card (button and note included) is wrapped in a bordered box while the sheet is on.
  const frame = () => {
    if (!card || sheet.dataset.twFrame !== '1') return;
    const from = sheet.dataset.twFrameFrom || 'meta';
    let first = card;
    if (from !== 'trigger') {
      const selector = from === 'price' ? '.tw-block--tw_price' : '.tw-block--tw_meta_rows';
      for (let el = card.previousElementSibling; el; el = el.previousElementSibling) {
        if (el.matches(selector)) { first = el; break; }
      }
    }
    const box = document.createElement('div');
    box.className = 'tw-sheet-frame';
    first.before(box);
    for (let node = first; node; ) {
      const next = node.nextElementSibling;
      // An empty block slot or a separator draws nothing but still takes a gap inside the panel,
      // so it travels with the rest and is hidden while the frame is up.
      if (node !== card && (node.offsetHeight < 2 || node.matches('[data-block-type="separator"]'))) {
        node.classList.add('tw-frame-void');
      }
      box.appendChild(node);
      if (node === card) break;
      node = next;
    }
  };

  const unframe = () => {
    document.querySelectorAll('.tw-frame-void').forEach((el) => el.classList.remove('tw-frame-void'));
    document.querySelectorAll('.tw-sheet-frame').forEach((box) => box.replaceWith(...box.childNodes));
  };

  const sync = () => { if (mq.matches) activate(); else deactivate(); };

  /**
   * The sheet holds the page's own picker and buy button, moved into it. If anything put them back
   * — a re-render we did not see, a restored page, a script that replaced the column — the sheet
   * would open empty, which is what a reader reported. So it looks before it opens: empty means
   * move them in again, and if they cannot be found at all it steps aside and takes the reader to
   * the picker in the page rather than showing an empty panel.
   */
  const filled = () => sheet.querySelector('[data-block-type="variant-picker"], [data-block-type="buy-buttons"]');

  // Healthy means: the sheet holds the blocks, the booking row is in the page, and the page holds
  // no second copy of the picker. Anything else and the two are rebuilt from scratch.
  const healthy = () => filled()
    && card?.isConnected
    && !document.querySelector('.product-info [data-block-type="variant-picker"]');

  const repair = () => {
    if (!mq.matches || healthy()) return;
    deactivate();
    activate();
  };

  // The theme swaps the whole buying column for a product change, taking the booking row with it.
  // That swap replaces <product-rerender> inside its parent, so the parent is what to watch.
  const rerenderHost = document.querySelector('product-rerender[id^="product-info"]');
  if (rerenderHost?.parentElement) {
    new MutationObserver(() => {
      if (mq.matches && !card?.isConnected) { deactivate(); setTimeout(sync, 0); }
    }).observe(rerenderHost.parentElement, { childList: true });
  }

  // A drawer opens by taking an "open" attribute, from a click anywhere, and it moves itself to
  // <body> while it is open — so wrapping show() missed most of the ways it can appear. These two
  // catch all of them: the click, before the theme acts on it, and the drawer's own after-show.
  const adopt = (element) => {
    if (element === sheet) return;
    // A second sheet in the page (a re-rendered section) leaves our blocks in the old one.
    deactivate();
    sheet = element;
    sheet.dataset.twSheetActive = '0';
  };

  const beforeOpen = (element) => {
    // Whatever goes wrong here, the reader must still be able to book: a throw falls through to
    // the picker in the page rather than leaving an empty panel open.
    try {
      adopt(element);
      repair();
      return filled();
    } catch {
      return false;
    }
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest?.('[aria-controls], .tw-sticky__button, .tw-sheet-cta');
    if (!trigger || !mq.matches) return;
    const controls = trigger.getAttribute('aria-controls');
    const element = controls ? document.getElementById(controls) : document.querySelector('[data-tw-sheet]');
    if (!element || !element.hasAttribute('data-tw-sheet')) return;
    beforeOpen(element);
  }, true);

  document.addEventListener('dialog:after-show', (event) => {
    const element = event.target;
    if (!element?.hasAttribute?.('data-tw-sheet')) return;
    if (beforeOpen(element)) return;
    // Nothing to book with: an empty panel helps nobody, so step aside and show the picker itself.
    const fallback = document.querySelector('.product-info [data-block-type="variant-picker"], .product-info [data-block-type="buy-buttons"]');
    if (!fallback) return;
    element.hide?.();
    fallback.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, true);

  // Once the booking is in the cart the sheet has done its job: close it so the cart drawer that
  // opens next is not hidden behind it.
  document.addEventListener('variant:add', () => {
    if (sheet.dataset.twSheetActive === '1' && sheet.hasAttribute('open')) sheet.hide?.();
  });

  sync();
  mq.addEventListener('change', sync);
  document.addEventListener('shopify:section:load', () => { deactivate(); setTimeout(sync, 50); });

  // Repeat Impact's partial re-render for the blocks we hold. The listener sits on the document in
  // the capture phase because a product change replaces the form element itself, and a listener
  // bound to that element would go with it.
  document.addEventListener('product:rerender', (event) => {
    if (sheet.dataset.twSheetActive !== '1') return;

    // A product change replaces the whole column: our placeholders and trigger card are gone and a
    // fresh picker sits in the page. Start again rather than keep a card nobody can see.
    const freshInPage = document.querySelector('.product-info [data-block-type="variant-picker"], .product-info [data-block-type="buy-buttons"]');
    if (freshInPage && !card?.isConnected) {
      deactivate();
      activate();
      return;
    }

    const fragment = event.detail.htmlFragment;
    ['variant-picker', 'quantity-selector', 'buy-buttons'].forEach((type) => {
      sheet.querySelectorAll(`[data-block-type="${type}"]`).forEach((element) => {
        const fresh = fragment.querySelector(`[data-block-type="${type}"][data-block-id="${element.getAttribute('data-block-id')}"]`);
        if (!fresh) return;
        fresh.querySelectorAll('noscript').forEach((n) => n.remove());
        if (type === 'buy-buttons') {
          const current = element.querySelector('buy-buttons');
          const next = fresh.querySelector('buy-buttons');
          if (current && next) current.replaceWith(next);
        } else if (type === 'quantity-selector') {
          const keep = element.querySelector('.quantity-selector__input')?.quantity;
          const home = homes.get(element);
          element.replaceWith(fresh);
          if (home) { homes.delete(element); homes.set(fresh, home); }
          const input = fresh.querySelector('.quantity-selector__input');
          if (input && keep) input.quantity = keep;
        } else {
          const home = homes.get(element);
          element.replaceWith(fresh);
          if (home) { homes.delete(element); homes.set(fresh, home); }
        }
      });
    });
    setTimeout(refresh, 0);
  }, true);

  document.addEventListener('variant:change', (event) => {
    if (event.detail?.variant?.price != null) price = event.detail.variant.price;
    refresh();
  }, true);

  document.addEventListener('change', (event) => {
    if (event.target.matches?.('input[name="quantity"]')) refresh();
  });
  document.addEventListener('input', (event) => {
    if (event.target.matches?.('input[name="quantity"]')) refresh();
  });
})();

/**
 * Gallery counter ("3 / 24") for the mobile carousel: follows Impact's carousel:change event.
 */
(() => {
  const bind = () => {
    document.querySelectorAll('[data-tw-gal-counter]').forEach((counter) => {
      if (counter.dataset.bound) return;
      counter.dataset.bound = '1';
      const carousel = counter.closest('product-gallery')?.querySelector('media-carousel');
      if (!carousel) return;
      carousel.addEventListener('carousel:change', (event) => {
        counter.textContent = (event.detail.index + 1) + ' / ' + counter.dataset.total;
      });
    });
  };
  bind();
  document.addEventListener('shopify:section:load', bind);
})();

/**
 * Month chips (variant picker -> "Group this option by month").
 *
 * The months are rendered by Liquid from the date option's own values; this only filters the
 * date buttons and, when the chosen month holds none of the current selection, moves the
 * selection to that month's first available date so the label and the variant stay in step.
 */
(() => {
  const apply = (group, key) => {
    const picker = group.closest('variant-picker');
    const dates = picker?.querySelector(`[data-tw-month-dates="${group.dataset.twMonthGroup}"]`);
    if (!dates) return;

    group.querySelectorAll('[data-tw-month]').forEach((chip) => {
      const on = chip.dataset.twMonth === key;
      chip.classList.toggle('is-active', on);
      chip.setAttribute('aria-pressed', String(on));
    });
    const current = group.querySelector('[data-tw-month-current]');
    const chip = group.querySelector(`[data-tw-month="${CSS.escape(key)}"]`);
    if (current && chip) current.textContent = chip.textContent.trim();

    let checkedVisible = false;
    const visible = [];
    dates.querySelectorAll('.tw-vp-date').forEach((cell) => {
      const on = cell.dataset.twMonth === key;
      cell.hidden = !on;
      if (!on) return;
      const input = cell.querySelector('input[type="radio"]');
      if (!input || input.disabled) return;
      visible.push(input);
      if (input.checked) checkedVisible = true;
    });

    // The chosen month holds none of the current pick: take its first date instead.
    if (!checkedVisible && visible.length) visible[0].click();
  };

  const sync = () => {
    document.querySelectorAll('[data-tw-month-group]').forEach((group) => {
      const active = group.querySelector('.tw-vp-month.is-active') || group.querySelector('[data-tw-month]');
      if (active) apply(group, active.dataset.twMonth);
    });
  };

  sync();
  document.addEventListener('shopify:section:load', sync);
  // Impact re-renders the picker on every variant change; the fresh markup needs filtering again.
  document.addEventListener('variant:change', () => setTimeout(sync, 0));

  document.addEventListener('click', (event) => {
    const chip = event.target.closest('.tw-vp-month');
    if (!chip) return;
    const group = chip.closest('[data-tw-month-group]');
    if (group) apply(group, chip.dataset.twMonth);
  });
})();

/**
 * Help centre inside a policy panel (TW policy cards -> "Questions" style articles).
 *
 * One panel holds the list of questions and every answer. Opening a question swaps the list for
 * that answer and a Back link brings the list back, so nothing is hidden behind a closed row.
 */
(() => {
  const show = (root, key) => {
    root.querySelectorAll('[data-tw-help-article]').forEach((article) => {
      article.hidden = article.dataset.twHelpArticle !== key;
    });
    const index = root.querySelector('[data-tw-help-index]');
    if (index) index.hidden = key !== null;

    // The panel is already scrolled where the reader left the list; every view starts at its top.
    const drawer = root.closest('x-drawer');
    const body = drawer?.shadowRoot?.querySelector('[part~="body"]');
    if (body) body.scrollTop = 0;
  };

  document.addEventListener('click', (event) => {
    const open = event.target.closest('[data-tw-help-open]');
    if (open) {
      const root = open.closest('[data-tw-help]');
      const key = open.dataset.twHelpOpen;
      show(root, key);
      root.querySelector(`[data-tw-help-article="${key}"] [data-tw-help-back]`)?.focus();
      return;
    }
    const back = event.target.closest('[data-tw-help-back]');
    if (back) show(back.closest('[data-tw-help]'), null);
  });

  document.addEventListener('input', (event) => {
    const field = event.target.closest('[data-tw-help-search]');
    if (!field) return;
    const query = field.value.trim().toLowerCase();
    const root = field.closest('[data-tw-help]');
    let found = false;
    root.querySelectorAll('[data-tw-help-index] .tw-help__list > li').forEach((row) => {
      const match = !query || row.textContent.toLowerCase().includes(query);
      row.hidden = !match;
      if (match) found = true;
    });
    const empty = root.querySelector('[data-tw-help-empty]');
    if (empty) empty.hidden = found;
  });
})();

/**
 * Picking a variant with the mouse must not move the page.
 *
 * The option inputs are visually hidden, so clicking a label focuses a 1px box that the browser
 * then scrolls into view — the page jumped several hundred pixels when the row sat near the fold.
 * Focus still moves, so the keyboard path is untouched; only the scroll that followed a pointer
 * is put back.
 */
(() => {
  let pointerY = null;

  document.addEventListener('pointerdown', (event) => {
    pointerY = event.target.closest && event.target.closest('variant-picker') ? window.scrollY : null;
  }, true);

  document.addEventListener('focusin', (event) => {
    if (pointerY === null) return;
    if (!event.target.matches || !event.target.matches('variant-picker input')) return;
    const from = pointerY;
    pointerY = null;
    requestAnimationFrame(() => {
      if (Math.abs(window.scrollY - from) > 2) window.scrollTo({ top: from, behavior: 'instant' });
    });
  });
})();

/**
 * Venue for the chosen date (TW venue block).
 *
 * A city can run the same workshop at more than one address, and which one depends on the date.
 * Every venue is in the page already; this shows the one whose date list holds the current pick.
 * Matching is forgiving on purpose — "Sat Oct 17", "October 17th" and "Saturday October 17th" all
 * name the same day — so the merchant can type the list the short way.
 */
(() => {
  const roots = document.querySelectorAll('[data-tw-venues]');
  if (!roots.length) return;

  const MONTHS = ['january', 'february', 'march', 'april', 'may', 'june',
    'july', 'august', 'september', 'october', 'november', 'december'];

  // A day is its month and its number. The variant says "Saturday October 17th", the metafield may
  // say "October 17th 2026" or "Oct 17" — the weekday and the year are noise for the match.
  const dayKey = (text) => {
    const value = (text || '').toLowerCase();
    const month = MONTHS.findIndex((name) => value.includes(name.slice(0, 3)));
    if (month < 0) return '';
    const day = (value.match(/\b(\d{1,2})(st|nd|rd|th)?\b/) || [])[1];
    return day ? (month + 1) + '-' + Number(day) : '';
  };

  const show = (root, dateLabel) => {
    const wanted = dayKey(dateLabel);
    const cards = Array.from(root.querySelectorAll('[data-tw-venue]'));
    if (!cards.length) return;

    let match = null;
    if (wanted) {
      match = cards.find((card) => (card.dataset.twVenueDates || '')
        .split(',')
        .map(dayKey)
        .filter(Boolean)
        .some((day) => day === wanted));
    }
    if (!match) match = cards.find((card) => card.hasAttribute('data-tw-venue-default')) || cards[0];
    cards.forEach((card) => { card.hidden = card !== match; });

    // On a phone the venue block is far below the fold, so the booking sheet carries a short copy
    // of it. Filled from the same matched card, which is why the two can never disagree.
    const line = document.querySelector('[data-tw-sheet-venue]');
    if (line && root.dataset.twVenueSheet !== '0') {
      const text = (selector) => match.querySelector(selector)?.textContent.trim() || '';
      const name = text('.tw-venue__name');
      const address = text('.tw-venue__address');
      line.querySelector('[data-tw-sheet-venue-name]').textContent = name;
      line.querySelector('[data-tw-sheet-venue-address]').textContent = address;
      line.hidden = !name && !address;
    }

    // The heading says what this address is; the chip beside it says which date it is for.
    const chip = root.querySelector('[data-tw-venue-for]');
    if (chip) {
      const label = (dateLabel || '').trim();
      chip.textContent = label
        ? (chip.dataset.twVenueForTemplate || 'Location for [date]').replace('[date]', label)
        : '';
      chip.hidden = !label;
    }
  };

  // The date option's position is worked out by Liquid; the variant carries its values in order.
  const dateFrom = (root, variant) => {
    const position = parseInt(root.dataset.twVenueOption, 10);
    if (!position || !variant) return '';
    return (variant.options || [])[position - 1] || '';
  };

  const refresh = (variant) => {
    roots.forEach((root) => {
      if (variant) { show(root, dateFrom(root, variant)); return; }
      // No variant in hand (first load): read the label the picker is showing.
      const picker = document.querySelector('variant-picker');
      const fieldset = picker?.querySelector('[data-tw-month-dates]');
      const label = fieldset?.querySelector('.variant-picker__option-info .h-stack > span:last-child');
      if (label) show(root, label.textContent);
    });
  };

  // A page whose date label could not be read yet would otherwise show no venue at all until the
  // reader picks a date; the default venue is a better first answer than an empty block.
  const ensureDefault = () => {
    roots.forEach((root) => {
      const cards = Array.from(root.querySelectorAll('[data-tw-venue]'));
      if (!cards.length || cards.some((card) => !card.hidden)) return;
      const first = cards.find((card) => card.hasAttribute('data-tw-venue-default')) || cards[0];
      first.hidden = false;
    });
  };

  refresh(null);
  ensureDefault();
  window.addEventListener('load', () => { refresh(null); ensureDefault(); });
  document.addEventListener('variant:change', (event) => refresh(event.detail?.variant));
  document.addEventListener('shopify:section:load', () => { refresh(null); ensureDefault(); });
})();

/**
 * Collapsed timeline stops (tw_timeline -> "Start stops collapsed").
 * The title is a button; clicking it reveals that stop's text and media. Nothing else on the
 * page depends on the state, so a class on the stop is all that is needed.
 */
document.addEventListener('click', (event) => {
  const toggle = event.target.closest('[data-tw-tl-toggle]');
  if (!toggle) return;

  const stop = toggle.closest('.tw-tl__stop');
  if (!stop) return;

  const open = !stop.classList.contains('is-open');
  stop.classList.toggle('is-open', open);
  toggle.setAttribute('aria-expanded', String(open));
});

/**
 * See more (tw_see_more): the blocks after the divider stay in the DOM, clipped to a peek that fades
 * into the page, so search engines and the anchor nav still see them. The button opens the fold;
 * when the merchant gave it a collapse label it also closes it again, and the page scrolls back
 * so the reader is not left staring at the bottom of a section that just shrank.
 */
function twRevealMore(more) {
  if (more.classList.contains('is-open')) return;
  more.classList.add('is-open');
  const toggle = more.querySelector('[data-tw-more-toggle]');
  if (!toggle) return;
  toggle.setAttribute('aria-expanded', 'true');
  if (toggle.dataset.twMoreClose) toggle.textContent = toggle.dataset.twMoreClose;
}

function twCollapseMore(more) {
  const toggle = more.querySelector('[data-tw-more-toggle]');
  if (!toggle?.dataset.twMoreClose) return;
  more.classList.remove('is-open');
  toggle.setAttribute('aria-expanded', 'false');
  toggle.textContent = toggle.dataset.twMoreOpen || toggle.textContent;
  if (more.getBoundingClientRect().top < 0) {
    more.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }
}

// The peek is clipped, not hidden, so a keyboard user can tab into it; when that happens the
// fold opens rather than leaving focus on something they cannot see.
document.addEventListener('focusin', (event) => {
  const body = event.target.closest('[data-tw-more-body]');
  const more = body?.closest('[data-tw-more]');
  if (more) twRevealMore(more);
});

document.addEventListener('click', (event) => {
  const toggle = event.target.closest('[data-tw-more-toggle]');
  if (!toggle) return;
  const more = toggle.closest('[data-tw-more]');
  if (!more) return;
  if (more.classList.contains('is-open')) twCollapseMore(more);
  else twRevealMore(more);
});

// In the theme editor, selecting a block that sits behind "See more" opens the fold so the
// editor's scroll-and-highlight lands on something visible.
document.addEventListener('shopify:block:select', (event) => {
  // The See more block's own element is the button inside the fold; selecting it must not open the fold.
  if (event.target?.matches?.('[data-tw-more-toggle]')) return;
  const more = event.target?.closest?.('[data-tw-more]');
  if (more) twRevealMore(more);
});

// The anchor nav scrolls to a target that may be folded; open the fold first, then scroll.
document.addEventListener('click', (event) => {
  const link = event.target.closest('.tw-nav a[href^="#"]');
  if (!link) return;
  const target = document.getElementById(link.getAttribute('href').slice(1));
  const more = target?.closest('[data-tw-more]');
  if (more) twRevealMore(more);
}, true);

/**
 * Blocks as accordions on a phone (TW blocks, when the section asks for it).
 *
 * Ten open sections is a long scroll on a 390px screen, so each block with a heading becomes a
 * tappable row and the reader opens what they want. Nothing is moved: the heading becomes the
 * control and CSS hides its siblings while the block is shut, so a block can be carried into the
 * booking sheet or re-rendered without any of this having to be undone first.
 */
(() => {
  const CLOSED = 'tw-acc-closed';
  // The width the stylesheet folds at. A heading is a control only where it actually folds.
  const FOLDING = window.matchMedia('(max-width: 749px)');

  const chevronFor = (head) => {
    let chevron = head.querySelector(':scope > .tw-acc__chevron');
    if (chevron) return chevron;
    chevron = document.createElement('span');
    chevron.className = 'tw-acc__chevron';
    chevron.setAttribute('aria-hidden', 'true');
    chevron.innerHTML = '<svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M4 6.5 8 10.5 12 6.5"/></svg>';
    head.appendChild(chevron);
    return chevron;
  };

  /**
   * On a wide screen the block is simply open, so the heading gives back role, tabindex and
   * aria-expanded. Leaving them on would tell a screen reader the heading is a button that does
   * nothing — and the theme styles every [role="button"] with a pointer cursor, which invites a
   * click that has no effect.
   */
  const init = () => {
    document.querySelectorAll('[data-tw-acc]').forEach((block) => {
      const head = block.querySelector('.tw-h');
      if (!head) return;
      head.classList.add('tw-acc__head');
      chevronFor(head);

      if (!FOLDING.matches) {
        head.removeAttribute('role');
        head.removeAttribute('tabindex');
        head.removeAttribute('aria-expanded');
        return;
      }

      head.setAttribute('role', 'button');
      head.setAttribute('tabindex', '0');

      // Shut unless the merchant marked this one to stay open. A block already wired keeps the
      // state the reader left it in.
      if (head.dataset.twAccReady !== '1') {
        head.dataset.twAccReady = '1';
        block.classList.toggle(CLOSED, !block.hasAttribute('data-tw-acc-open'));
      }
      head.setAttribute('aria-expanded', String(!block.classList.contains(CLOSED)));
    });
  };

  const toggle = (head) => {
    const block = head.closest('[data-tw-acc]');
    if (!block) return;
    const closed = block.classList.toggle(CLOSED);
    head.setAttribute('aria-expanded', String(!closed));
  };

  document.addEventListener('click', (event) => {
    if (!FOLDING.matches) return;
    const head = event.target.closest?.('[data-tw-acc] > .tw-acc__head');
    if (head) toggle(head);
  });

  document.addEventListener('keydown', (event) => {
    if (!FOLDING.matches) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const head = event.target.closest?.('[data-tw-acc] > .tw-acc__head');
    if (!head) return;
    event.preventDefault();
    toggle(head);
  });

  init();
  FOLDING.addEventListener('change', init);
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('product:rerender', init, true);
  document.addEventListener('variant:change', init, true);
})();

/**
 * The lamp strip, scrollable with a mouse.
 *
 * A finger drags a row of cards without being told it can; a pointer has no such gesture. The
 * arrows are in the markup now, so they land in the place the merchant chose without a frame
 * appearing after paint; this only wires them and hides the pair where nothing overflows.
 */
(() => {
  const wire = () => {
    document.querySelectorAll('.tw-lampgrid--strip').forEach((strip) => {
      if (strip.dataset.twStripReady === '1') return;

      // The arrows sit either before the rail or inside it, so look in the block, not a sibling.
      const nav = strip.closest('.tw-block')?.querySelector('[data-tw-strip-nav]');
      if (!nav) return;
      strip.dataset.twStripReady = '1';

      const buttons = Array.from(nav.querySelectorAll('[data-dir]'));

      // A step is a screenful less one card, so nothing is skipped over unseen.
      const step = () => {
        const card = strip.querySelector('.tw-lampgrid__card');
        const width = card ? card.getBoundingClientRect().width + 10 : 150;
        return Math.max(width, strip.clientWidth - width);
      };

      const sync = () => {
        const slack = strip.scrollWidth - strip.clientWidth;
        nav.hidden = slack < 8;
        buttons[0].disabled = strip.scrollLeft < 8;
        buttons[1].disabled = strip.scrollLeft > slack - 8;
      };

      buttons.forEach((button) => button.addEventListener('click', () => {
        strip.scrollBy({ left: step() * Number(button.dataset.dir), behavior: 'smooth' });
      }));

      // A mouse can grab the row and drag it, as a finger does. A drag longer than a few pixels
      // swallows the click that ends it, so letting go over a card does not open its zoom.
      let drag = null;
      let draggedAt = 0;
      strip.addEventListener('click', (event) => {
        if (Date.now() - draggedAt < 150) { event.preventDefault(); event.stopPropagation(); }
      }, true);
      strip.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        drag = { x: event.clientX, left: strip.scrollLeft, moved: false };
      });
      strip.addEventListener('pointermove', (event) => {
        if (!drag) return;
        const dx = event.clientX - drag.x;
        if (!drag.moved && Math.abs(dx) < 5) return;
        if (!drag.moved) {
          drag.moved = true;
          strip.classList.add('is-dragging');
          strip.setPointerCapture(event.pointerId);
        }
        strip.scrollLeft = drag.left - dx;
      });
      const release = () => {
        if (!drag) return;
        const moved = drag.moved;
        drag = null;
        if (!moved) return;
        // Snap again from where the drag ended, not from where it began.
        const left = strip.scrollLeft;
        strip.classList.remove('is-dragging');
        strip.scrollLeft = left;
        draggedAt = Date.now();
      };
      strip.addEventListener('pointerup', release);
      strip.addEventListener('pointercancel', release);
      strip.addEventListener('dragstart', (event) => event.preventDefault());

      strip.addEventListener('scroll', sync, { passive: true });
      window.addEventListener('resize', sync);
      // The pictures decide the width, so the first measurement waits for them.
      window.addEventListener('load', sync);
      sync();
    });
  };

  wire();
  document.addEventListener('shopify:section:load', wire);
})();

/**
 * TW use cases as a slider: the same arrows as the lamp strip, one card per step.
 */
(() => {
  const wire = () => {
    document.querySelectorAll('[data-tw-uc]').forEach((box) => {
      const list = box.querySelector('.tw-uc__list');
      const nav = box.querySelector('[data-tw-uc-nav]');
      if (!list || !nav || box.dataset.twUcReady === '1') return;
      box.dataset.twUcReady = '1';
      const buttons = Array.from(nav.querySelectorAll('[data-dir]'));
      const step = () => {
        const card = list.querySelector('.tw-uc__card');
        const gap = parseFloat(getComputedStyle(list).columnGap) || 0;
        return card ? card.getBoundingClientRect().width + gap : list.clientWidth;
      };
      const sync = () => {
        const slack = list.scrollWidth - list.clientWidth;
        nav.hidden = slack < 8;
        buttons[0].disabled = list.scrollLeft < 8;
        buttons[1].disabled = list.scrollLeft > slack - 8;
      };
      buttons.forEach((button) => button.addEventListener('click', () => {
        list.scrollBy({ left: step() * Number(button.dataset.dir), behavior: 'smooth' });
      }));
      list.addEventListener('scroll', sync, { passive: true });
      window.addEventListener('resize', sync);
      sync();
    });
  };
  wire();
  document.addEventListener('shopify:section:load', wire);
})();

/**
 * TW price follows the selected variant.
 *
 * The theme's partial re-render (on a variant change) only swaps blocks whose type is on a fixed list
 * (price, inventory, buy buttons ...). tw_price is not on it, so the number kept showing the variant
 * the page loaded with while the sticky bar, which re-renders whole, showed the new one. This listens to
 * the same event the theme does (it does not bubble, so the capture phase on the document) and swaps in
 * the fresh price block from the same response.
 */
(() => {
  const SELECTOR = '.tw-block--tw_price';
  const collect = (root, found = []) => {
    root.querySelectorAll(SELECTOR).forEach((el) => found.push(el));
    root.querySelectorAll('template').forEach((t) => collect(t.content, found));
    return found;
  };
  document.addEventListener('product:rerender', (event) => {
    const fragment = event.detail && event.detail.htmlFragment;
    // A product change replaces the whole section anyway.
    if (!fragment || event.detail.productChange) return;
    const fresh = collect(fragment);
    const current = Array.from(document.querySelectorAll(SELECTOR));
    if (!fresh.length || fresh.length !== current.length) return;
    current.forEach((el, i) => el.replaceWith(fresh[i]));
  }, true);
})();

/**
 * Sticky bar on phones and tablets.
 *
 * The theme opens the bar once the product form has scrolled out of view above. Below 1000px the form
 * moves into the booking sheet (a drawer kept at the end of the page), so it never scrolls past and the
 * bar never opened. While the sheet is on, the bar follows the booking card instead: it appears once the
 * card has scrolled up out of view and goes again when the footer shows. On a desktop there is no card and
 * the theme's own rule applies.
 *
 * The position is read on scroll rather than from an IntersectionObserver: that only reports when something
 * starts or stops intersecting, and a card that begins below the first screen never reports a page opened
 * (or jumped) further down.
 */
(() => {
  let queued = false;

  // The theme's own bar opens when the product form scrolls out of view, which it reads from the form's box.
  // Beside the quantity box the form is drawn with display: contents (tw-product.css, the buy row), so it has
  // no box and the theme never opens the bar. The Add to cart button stands in for it.
  const mainAddToCart = () => Array.from(document.querySelectorAll('buy-buttons button[type="submit"], buy-buttons .button'))
    .find((b) => !b.closest('product-quick-add') && b.getBoundingClientRect().height > 0);

  const update = () => {
    queued = false;
    const bar = document.querySelector('.product-quick-add');
    if (!bar) return;
    const footer = document.querySelector('.footer');
    let on = false;
    if (bar.classList.contains('tw-sticky')) {
      // The event bar follows the booking card; with no card (the sheet is off) it follows the form like the
      // standard bar does, by way of the Add to cart button when the form has no box.
      const card = document.querySelector('.tw-sheet-card');
      let anchor = card;
      if (!anchor) {
        const form = document.getElementById(bar.getAttribute('form'));
        if (form && getComputedStyle(form).display === 'contents') anchor = mainAddToCart();
      }
      if (anchor && footer) {
        on = anchor.getBoundingClientRect().bottom < 0 && footer.getBoundingClientRect().top >= window.innerHeight;
      }
    } else {
      const form = document.getElementById(bar.getAttribute('form'));
      const boxless = form && getComputedStyle(form).display === 'contents';
      const anchor = boxless ? mainAddToCart() : null;
      if (anchor && footer) {
        on = anchor.getBoundingClientRect().bottom < 0 && footer.getBoundingClientRect().top >= window.innerHeight;
      }
    }
    bar.classList.toggle('tw-sticky--on', on);
    liftChat(bar.classList.contains('is-visible') || on ? bar.offsetHeight : 0);
  };

  // The store's chat button (Shopify's <shopify-chat>) sits in the bottom corner, right where the bar
  // is. Its own stylesheet lives in an open shadow root, so while the bar is showing the button is lifted
  // by the bar's height with a rule added there. Without the chat app, or if its markup changes, this does nothing.
  let chatSheet = null;
  let chatLift = -1;
  const liftChat = (px) => {
    if (px === chatLift) return;
    const root = document.querySelector('shopify-chat')?.shadowRoot?.querySelector('shopify-agent-runtime')?.shadowRoot;
    if (!root || typeof CSSStyleSheet === 'undefined' || !('adoptedStyleSheets' in root)) return;
    try {
      if (!chatSheet) {
        chatSheet = new CSSStyleSheet();
        root.adoptedStyleSheets = [...root.adoptedStyleSheets, chatSheet];
      }
      chatSheet.replaceSync(`.activator-nudge { margin-bottom: ${px}px !important; transition: margin-bottom .2s ease; }`);
      chatLift = px;
    } catch (e) { /* a browser without constructable sheets: the button stays where it is */ }
  };

  const queue = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(update);
  };

  window.addEventListener('scroll', queue, { passive: true });
  window.addEventListener('resize', queue);
  // The card is built after the page has loaded and again whenever the sheet switches on or off, and the
  // bar is rebuilt on every variant change, which drops its class: check again after each of those.
  // Only the bar, the card or the form being added or removed matters; the hundreds of other DOM changes while a page
  // loads (apps, tracking, gallery) each used to trigger a measurement that forces a layout.
  const RELEVANT = '.product-quick-add, .tw-sheet-card, buy-buttons';
  const touches = (node) => node.nodeType === 1 && (node.matches(RELEVANT) || node.querySelector(RELEVANT) !== null);
  new MutationObserver((mutations) => {
    if (mutations.some((m) => Array.from(m.addedNodes).some(touches) || Array.from(m.removedNodes).some(touches))) queue();
  }).observe(document.body, { childList: true, subtree: true });
  document.addEventListener('product:rerender', queue, true);
  document.addEventListener('shopify:section:load', queue);
  queue();
})();

/**
 * <tw-delivery> (TW delivery estimate block): "Order today, arrives by Oct 14-16".
 *
 * Business days are Monday to Friday minus the listed holidays. The order day is today in the shop's time
 * zone, or the next business day when the order comes in at or after the cut-off hour (or at a weekend or
 * on a holiday). The parcel ships after the handling days, then travels for the fastest and the slowest
 * number of business days. twDeliveryDates is plain date arithmetic on UTC calendar days.
 */
// twDeliveryDates:start
function twDeliveryDates(nowParts, opts) {
  const day = (y, m, d) => Date.UTC(y, m - 1, d);
  const key = (t) => new Date(t).toISOString().slice(0, 10);
  const off = new Set(opts.holidays);
  const business = (t) => { const w = new Date(t).getUTCDay(); return w !== 0 && w !== 6 && !off.has(key(t)); };
  const next = (t) => { do { t += 86400000; } while (!business(t)); return t; };
  const add = (t, n) => { for (let i = 0; i < n; i++) t = next(t); return t; };
  let start = day(nowParts.y, nowParts.m, nowParts.d);
  if (nowParts.h >= opts.cutoff || !business(start)) start = next(start);
  const ship = add(start, opts.handling);
  return { earliest: add(ship, opts.min), latest: add(ship, opts.max) };
}
// twDeliveryDates:end

class TwDelivery extends HTMLElement {
  connectedCallback() {
    try { this.render(); } catch (e) { /* the written fallback stays */ }
  }
  render() {
    const d = this.dataset;
    const zone = d.zone || 'America/Chicago';
    const parts = {};
    new Intl.DateTimeFormat('en-US', { timeZone: zone, year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', hourCycle: 'h23' })
      .formatToParts(new Date()).forEach((p) => { if (p.type !== 'literal') parts[p.type] = parseInt(p.value, 10); });
    const range = twDeliveryDates({ y: parts.year, m: parts.month, d: parts.day, h: parts.hour }, {
      handling: parseInt(d.handling, 10) || 0,
      min: parseInt(d.min, 10) || 1,
      max: Math.max(parseInt(d.max, 10) || 1, parseInt(d.min, 10) || 1),
      cutoff: Number.isNaN(parseInt(d.cutoff, 10)) ? 14 : parseInt(d.cutoff, 10),
      holidays: (d.holidays || '').split(',').map((x) => x.trim()).filter(Boolean)
    });
    const month = (t) => new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', month: 'short' }).format(t);
    const dom = (t) => new Date(t).getUTCDate();
    const full = (t) => month(t) + ' ' + dom(t);
    let text;
    if (d.format === 'earliest') text = full(range.earliest);
    else if (d.format === 'latest' || range.earliest === range.latest) text = full(range.latest);
    else if (month(range.earliest) === month(range.latest)) text = month(range.earliest) + ' ' + dom(range.earliest) + '\u2013' + dom(range.latest);
    else text = full(range.earliest) + '\u2013' + full(range.latest);
    const slot = this.querySelector('[data-tw-date]');
    const live = this.querySelector('.tw-delivery__live');
    const fallback = this.querySelector('.tw-delivery__fallback');
    if (!slot || !live) return;
    slot.textContent = text;
    live.hidden = false;
    if (fallback) fallback.hidden = true;
  }
}
if (!window.customElements.get('tw-delivery')) window.customElements.define('tw-delivery', TwDelivery);

/**
 * <tw-flow> (TW how it works section): an accordion slider. A click on a panel opens it and closes the others; with
 * data-mode="hover" a mouse hover does the same on a desktop. The text of a closed panel stays in the page (clipped,
 * and inert so it cannot be focused); opening only toggles classes and attributes, nothing is fetched. The video
 * panel creates its <video> element when the play button is pressed, so nothing heavy loads before that.
 */
class TwFlow extends HTMLElement {
  connectedCallback() {
    this.panels = Array.from(this.querySelectorAll('[data-tw-flow-panel]'));
    this.hoverQuery = window.matchMedia('(hover: hover) and (min-width: 750px)');
    this.addEventListener('click', (event) => {
      const play = event.target.closest('[data-tw-flow-play]');
      if (play) { this.play(play); return; }
      const trigger = event.target.closest('.tw-flow__trigger');
      if (trigger) this.open(trigger.closest('[data-tw-flow-panel]'));
    });
    this.addEventListener('mouseover', (event) => {
      if (this.dataset.mode !== 'hover' || !this.hoverQuery.matches) return;
      const panel = event.target.closest('[data-tw-flow-panel]');
      if (panel && !panel.classList.contains('is-open')) this.open(panel);
    });
    this.addEventListener('keydown', (event) => {
      const keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
      const trigger = event.target.closest('.tw-flow__trigger');
      if (!trigger || !(event.key in keys)) return;
      const triggers = Array.from(this.querySelectorAll('.tw-flow__trigger'));
      const next = triggers[(triggers.indexOf(trigger) + keys[event.key] + triggers.length) % triggers.length];
      event.preventDefault();
      next.focus();
    });
  }
  open(target) {
    this.panels.forEach((panel) => {
      const on = panel === target;
      panel.classList.toggle('is-open', on);
      panel.querySelector('.tw-flow__trigger').setAttribute('aria-expanded', String(on));
      panel.querySelector('.tw-flow__body').toggleAttribute('inert', !on);
      const video = panel.querySelector('.tw-flow__video');
      if (video && !on) video.pause();
    });
  }
  play(button) {
    const host = button.closest('[data-tw-flow-panel], [data-tw-flow-band]');
    if (host.hasAttribute('data-popup') && this.querySelector('[data-tw-flow-dialog]')) { this.openPopup(button); return; }
    let media;
    if (button.dataset.embed) {
      media = document.createElement('iframe');
      media.src = button.dataset.embed;
      media.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
      media.allowFullscreen = true;
      media.title = button.getAttribute('aria-label') || '';
    } else {
      media = document.createElement('video');
      media.src = button.dataset.src;
      media.controls = true;
      media.autoplay = true;
      media.playsInline = true;
    }
    media.className = 'tw-flow__video';
    host.prepend(media);
    host.classList.add('is-playing');
    button.hidden = true;
    if (host.hasAttribute('data-tw-flow-band')) this.growBand(host);
  }
  /** The pop-up: the player is created inside the dialog when the button is pressed and thrown away when it closes. */
  openPopup(button) {
    const dialog = this.querySelector('[data-tw-flow-dialog]');
    const body = dialog.querySelector('[data-tw-flow-dialog-body]');
    let media;
    if (button.dataset.embed) {
      media = document.createElement('iframe');
      media.src = button.dataset.embed;
      media.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
      media.allowFullscreen = true;
      media.title = button.getAttribute('aria-label') || '';
    } else {
      media = document.createElement('video');
      media.src = button.dataset.src;
      media.controls = true;
      media.autoplay = true;
      media.playsInline = true;
    }
    body.replaceChildren(media);
    if (!dialog.dataset.bound) {
      dialog.dataset.bound = '1';
      dialog.addEventListener('click', (event) => { if (event.target === dialog || event.target.closest('[data-tw-flow-close]')) dialog.close(); });
      dialog.addEventListener('close', () => { body.replaceChildren(); document.documentElement.style.overflow = ''; });
    }
    document.documentElement.style.overflow = 'hidden';
    dialog.showModal();
  }
  /** The low strip grows to the video's own shape; afterwards the height follows the width again. */
  growBand(host) {
    const ratio = parseFloat(host.dataset.ratio) || 16 / 9;
    const target = Math.round(host.offsetWidth / ratio);
    host.style.height = host.offsetHeight + 'px';
    requestAnimationFrame(() => { host.style.height = target + 'px'; });
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      host.style.height = 'auto';
      host.style.aspectRatio = String(ratio);
    };
    host.addEventListener('transitionend', finish, { once: true });
    window.setTimeout(finish, 900);
  }
}
if (!window.customElements.get('tw-flow')) window.customElements.define('tw-flow', TwFlow);

/**
 * Never leave a sold-out booking selected.
 *
 * A link with ?variant= of a sold-out slot, or a new time that keeps a lamp sold out at that time,
 * would show the page with nothing to book. When the selected variant is not available, pick the
 * first available value, starting from the last option (the lamp) and moving up to the time and
 * the date only when nothing below is left. Impact re-renders after each change, so this runs
 * again on the next 'variant:change' until the selection is bookable; a counter stops any loop.
 */
(() => {
  let tries = 0;

  const fix = () => {
    const picker = document.querySelector('.product-info variant-picker, [data-tw-sheet] variant-picker');
    if (!picker || !picker.selectedVariant || picker.selectedVariant.available !== false) { tries = 0; return; }
    if (tries++ > 4) return;

    const formId = picker.getAttribute('form-id');
    const inputs = Array.from(document.querySelectorAll(`input[data-option-position][form="${CSS.escape(formId)}"]`));
    const positions = [...new Set(inputs.map((input) => Number(input.dataset.optionPosition)))].sort((a, b) => b - a);

    const shut = (input) => (document.querySelector('label[for="' + CSS.escape(input.id) + '"]') || input.nextElementSibling)?.classList.contains('is-disabled');
    for (const position of positions) {
      const group = inputs.filter((input) => Number(input.dataset.optionPosition) === position);
      const open = group.find((input) => !input.checked && !shut(input));
      const current = group.find((input) => input.checked);
      if (current && !shut(current)) continue;
      if (!open) continue;
      open.checked = true;
      open.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }
  };

  // A sold-out lamp stays hoverable so its zoom button shows, but a click on it picks nothing.
  document.addEventListener('click', (event) => {
    const swatch = event.target.closest?.('variant-picker .thumbnail-swatch.is-disabled');
    if (!swatch || event.target.closest('.tw-vp__zoom')) return;
    event.preventDefault();
    event.stopPropagation();
  }, true);

  document.addEventListener('variant:change', () => setTimeout(fix, 0));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fix);
  else fix();
  // The picker is a custom element; give it a moment to read its variant on a slow page.
  window.addEventListener('load', fix);
})();
